import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, InputNumber, Modal, Select } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { fetchVenues } from './venueApi'
import {
  campaignDraftBody,
  createCampaign,
  updateCampaign,
  type AdCampaign,
  type CampaignFormValues,
} from './campaignApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface CampaignFormModalProps {
  open: boolean
  /** `null` = yangi kampaniya */
  campaign: AdCampaign | null
  onClose: () => void
}

/** Formada davr BITTA tanlagichda, so'rovda esa ikki sana — shuning uchun ayri tip. */
type FormValues = Omit<CampaignFormValues, 'startsAt' | 'endsAt'> & {
  period: [Dayjs, Dayjs]
}

const BYTES_IN_GB = 1024 ** 3

/**
 * Reklama kampaniyasi.
 *
 * ⚠️ Pul maydoni YO'Q va bo'lmasligi kerak — bu modul faqat ko'rsatish
 * faktini qayd etadi (`AdCampaign.java` javadoc). Hisob-kitob `ledger` da.
 */
export function CampaignFormModal({ open, campaign, onClose }: CampaignFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const venues = useQuery({ queryKey: queryKeys.venues.all, queryFn: () => fetchVenues(null), enabled: open })

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      campaign
        ? {
            venueId: campaign.venueId ?? undefined,
            name: campaign.name,
            advertiserName: campaign.advertiserName,
            creativeRef: campaign.creativeRef ?? undefined,
            rewardGb: campaign.rewardBytes === null ? null : campaign.rewardBytes / BYTES_IN_GB,
            rewardSeconds: campaign.rewardSeconds,
            minViewSeconds: campaign.minViewSeconds,
            dailyCapPerGuest: campaign.dailyCapPerGuest,
            period: [
              campaign.startsAt ? dayjs(campaign.startsAt) : dayjs(),
              campaign.endsAt ? dayjs(campaign.endsAt) : dayjs().add(30, 'day'),
            ],
          }
        : {
            period: [dayjs(), dayjs().add(30, 'day')],
          },
    )
  }, [open, campaign, form])

  /*
    ⚠️ `period` ATAYLAB BRAUZER ZONASIDA QOLDIRILDI — bu YAGONA istisno.

    Panelning qolgan hamma sana oynalari tenant zonasiga ko'chirildi
    (`shared/lib/dayRange.ts`), lekin bu yerdagi tanlagich `showTime`
    bilan ishlaydi: operator KALENDAR KUNINI emas, DEVOR SOATINI
    kiritadi ("13.08.2026 09:00").

    Mavjud kampaniya ochilganda qiymat `dayjs(campaign.startsAt)` bilan,
    ya'ni BRAUZER zonasida ko'rsatiladi. Kiritishni tenant zonasiga
    o'tkazib, ko'rsatishni qoldirish formani o'z-o'ziga zid qilardi:
    operator 09:00 yozadi, saqlaydi, qayta ochadi va 08:30 ko'radi.

    To'g'ri yechim — butun tanlagichni tenant zonasida chizish, bu esa
    `dayjs` ning `timezone` plagini yoki alohida o'ram komponenti degani.
    Bu KUN CHEGARASI muammosi emas va alohida qaror talab qiladi.
  */
  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // ⚠️ Tana KOMPONENTDA yig'ilmaydi: `campaignDraftBody` ni qorovul
      // CHAQIRIB o'lchaydi — `advertiserPartyId` ko'rinmasdan qaytariladi
      // va uni naqsh qidiruvi bilan tekshirib bo'lmasdi.
      const { period, ...rest } = values
      const draft = campaignDraftBody(
        { ...rest, startsAt: period[0].toISOString(), endsAt: period[1].toISOString() },
        campaign,
      )
      return campaign ? updateCampaign(campaign.id, draft) : createCampaign(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(null) })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={campaign ? t('campaign.editTitle') : t('campaign.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={campaign ? t('common.save') : t('operation.create')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item name="venueId" label={t('campaign.venue')} extra={t('campaign.venueHint')}>
          <Select
            allowClear
            loading={venues.isPending}
            placeholder={t('campaign.allVenues')}
            options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label={t('campaign.name')}
          rules={[{ required: true, message: t('campaign.nameRequired') }]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="advertiserName"
          label={t('campaign.advertiserName')}
          rules={[{ required: true, message: t('campaign.advertiserNameRequired') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="creativeRef" label={t('campaign.creativeRef')} extra={t('campaign.creativeRefHint')}>
          <Input />
        </Form.Item>

        <Form.Item name="rewardGb" label={t('campaign.rewardGb')} extra={t('campaign.rewardHint')}>
          <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="rewardSeconds" label={t('campaign.rewardSeconds')}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="minViewSeconds"
          label={t('campaign.minViewSeconds')}
          extra={t('campaign.minViewSecondsHint')}
        >
          <InputNumber min={1} max={300} style={{ width: '100%' }} placeholder="5" />
        </Form.Item>

        <Form.Item
          name="dailyCapPerGuest"
          label={t('campaign.dailyCapPerGuest')}
          extra={t('campaign.dailyCapPerGuestHint')}
        >
          <InputNumber min={1} max={100} style={{ width: '100%' }} placeholder="1" />
        </Form.Item>

        <Form.Item
          name="period"
          label={t('campaign.period')}
          rules={[{ required: true, message: t('campaign.periodRequired') }]}
        >
          <DatePicker.RangePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
